import './CabinetDashboardPage.scss';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Layers, FileCheck, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../i18n';
import { get } from '../../services/api';
import Card from '../../components/Card';
import OrderStatusBadge from '../../components/OrderStatusBadge';
import OrderProgressBar from '../../components/OrderProgressBar';
import Timeline from '../../components/Timeline';
import EmptyState from '../../components/EmptyState';
import Skeleton from '../../components/Skeleton';
import type { Notification, Order, Paginated } from '../../types';

interface DashboardStats {
  active_orders: number;
  stages_in_progress: number;
  documents_pending: number;
  unread_messages: number;
}

interface DashboardEvent {
  id: string;
  title: string;
  description?: string;
  created_at: string;
}

function orderProgress(o: Order): number {
  if (!o.stages || o.stages.length === 0) return 0;
  const done = o.stages.filter((s) => s.status === 'done').length;
  return Math.round((done / o.stages.length) * 100);
}

export default function CabinetDashboardPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [events, setEvents] = useState<DashboardEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await get<DashboardStats>('/me/dashboard-stats/');
        if (!cancelled) setStats(res);
      } catch {
        try {
          const ord = await get<Paginated<Order>>('/me/orders/');
          const notif = await get<Paginated<Notification>>('/me/notifications/?is_read=false');
          if (cancelled) return;
          const list = ord.results || [];
          const active = list.filter((o) => o.status === 'in_progress' || o.status === 'signed' || o.status === 'review');
          const stages = active.reduce((acc, o) => acc + (o.stages?.filter((s) => s.status === 'in_progress').length || 0), 0);
          setStats({
            active_orders: active.length,
            stages_in_progress: stages,
            documents_pending: 0,
            unread_messages: notif.count || (notif.results || []).length,
          });
        } catch {
          if (!cancelled) setStats({ active_orders: 0, stages_in_progress: 0, documents_pending: 0, unread_messages: 0 });
        }
      }
      try {
        const ord = await get<Paginated<Order>>('/me/orders/?status=in_progress&page_size=3');
        if (!cancelled) setOrders((ord.results || []).slice(0, 3));
      } catch {
        if (!cancelled) setOrders([]);
      }
      try {
        const ev = await get<Paginated<DashboardEvent>>('/me/events/?page_size=10');
        if (!cancelled) setEvents((ev.results || []).slice(0, 10));
      } catch {
        if (!cancelled) setEvents([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const name = user?.full_name || user?.email || '';
  const kpis = [
    { key: 'active_orders', label: t('cabinet.kpi.active_orders'), icon: <Briefcase size={20} />, value: stats?.active_orders ?? 0 },
    { key: 'stages', label: t('cabinet.kpi.stages'), icon: <Layers size={20} />, value: stats?.stages_in_progress ?? 0 },
    { key: 'documents', label: t('cabinet.kpi.documents'), icon: <FileCheck size={20} />, value: stats?.documents_pending ?? 0 },
    { key: 'messages', label: t('cabinet.kpi.messages'), icon: <MessageSquare size={20} />, value: stats?.unread_messages ?? 0 },
  ];

  return (
    <div className="dashboard">
      <Card padding="lg" className="dashboard__hello">
        <h1 className="dashboard__hello-title">{t('cabinet.greeting', { name })}</h1>
      </Card>

      <div className="dashboard__kpi">
        {kpis.map((k) => (
          <Card key={k.key} padding="md" className="dashboard__kpi-card">
            <span className="dashboard__kpi-icon" aria-hidden="true">{k.icon}</span>
            <div className="dashboard__kpi-info">
              <div className="dashboard__kpi-label">{k.label}</div>
              <div className="dashboard__kpi-value">{loading ? <Skeleton width={40} height={28} /> : k.value}</div>
            </div>
          </Card>
        ))}
      </div>

      <section className="dashboard__section">
        <h2 className="dashboard__section-title">{t('cabinet.active_orders')}</h2>
        {loading ? (
          <Skeleton height={120} />
        ) : orders.length === 0 ? (
          <EmptyState title={t('cabinet.no_orders')} />
        ) : (
          <div className="dashboard__orders">
            {orders.map((o) => (
              <Link key={o.public_id} to={`/cabinet/orders/${o.public_id}`} className="dashboard__order-link">
                <Card padding="md" className="dashboard__order">
                  <div className="dashboard__order-head">
                    <div>
                      <div className="dashboard__order-id">{o.human_id}</div>
                      <div className="dashboard__order-title">{o.title}</div>
                    </div>
                    <OrderStatusBadge status={o.status} />
                  </div>
                  <OrderProgressBar value={orderProgress(o)} max={100} showValue />
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="dashboard__section">
        <h2 className="dashboard__section-title">{t('cabinet.events')}</h2>
        {loading ? (
          <Skeleton height={120} />
        ) : events.length === 0 ? (
          <EmptyState title={t('cabinet.no_events')} />
        ) : (
          <Card padding="md">
            <Timeline events={events.map((e) => ({ id: e.id, title: e.title, description: e.description, time: e.created_at }))} />
          </Card>
        )}
      </section>
    </div>
  );
}
